import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Users,
  Camera,
  ClipboardList,
  UserCog,
  LogOut,
  Play,
  Sparkles,
  UserCircle2,
  Cloud,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function AppShell() {
  const { user, logout, viewAs, setViewAs, effectiveRole } = useAuth();
  const navigate = useNavigate();

  const isAdmin = user?.role === "admin";
  const showAdminNav = effectiveRole === "admin";

  const adminNav = [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/clients", label: "Clients", icon: Users },
    { to: "/shoots", label: "Shoots", icon: Camera },
    { to: "/tasks", label: "Tasks & Approvals", icon: ClipboardList },
    { to: "/editors", label: "Editors", icon: UserCog },
    { to: "/drive", label: "Drive", icon: Cloud },
  ];

  const editorNav = [
    { to: "/portal", label: "My Tasks", icon: ClipboardList },
  ];

  const nav = showAdminNav ? adminNav : editorNav;

  const seedDemo = async () => {
    try {
      await api.post("/seed");
      toast.success("Demo data seeded", { description: "Refreshing pages will show demo records." });
      window.location.reload();
    } catch (e) {
      toast.error("Seed failed", { description: e?.response?.data?.detail || "Try again" });
    }
  };

  return (
    <div className="min-h-screen flex bg-zinc-950 text-zinc-100">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-white/5 bg-zinc-950/80 backdrop-blur-xl flex flex-col sticky top-0 h-screen">
        <div className="px-6 pt-6 pb-8">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-white text-zinc-950 flex items-center justify-center">
              <Play className="h-4 w-4 fill-current" />
            </div>
            <div>
              <div className="font-display font-bold tracking-tight leading-none">Purnova Ops</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">Purnova's Space</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 space-y-1">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-white/10 text-white"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {isAdmin && (
          <div className="px-3 pb-3">
            <Button
              data-testid="seed-demo-btn"
              onClick={seedDemo}
              variant="outline"
              className="w-full border-white/10 bg-transparent text-zinc-300 hover:bg-white/5 hover:text-white"
            >
              <Sparkles className="h-4 w-4 mr-2" /> Seed Demo Data
            </Button>
          </div>
        )}

        {/* Role toggle for admin */}
        {isAdmin && (
          <div className="px-4 pb-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">View As</div>
            <div className="flex rounded-md border border-white/10 p-0.5 bg-zinc-900">
              {["admin", "editor"].map((r) => (
                <button
                  key={r}
                  data-testid={`viewas-${r}`}
                  onClick={() => {
                    setViewAs(r);
                    navigate(r === "admin" ? "/dashboard" : "/portal");
                  }}
                  className={`flex-1 text-xs py-1.5 rounded ${
                    (viewAs === r || (viewAs === "auto" && user.role === r))
                      ? "bg-white text-zinc-950 font-semibold"
                      : "text-zinc-400"
                  }`}
                >
                  {r === "admin" ? "Admin" : "Editor"}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="px-4 pb-6 pt-2 border-t border-white/5">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img
                src={user.picture}
                alt=""
                className="h-9 w-9 rounded-full object-cover border border-white/10"
              />
            ) : (
              <div className="h-9 w-9 rounded-full bg-zinc-800 flex items-center justify-center">
                <UserCircle2 className="h-5 w-5 text-zinc-400" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">
                {user?.role}
              </div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="h-8 w-8 rounded-md hover:bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
